import { blockColorMap } from '../../defs/block';
import { CollideEvent, CollideId } from '../../defs/collide';
import type { ChameleonBlockEntity } from '../../entities/BlockEntity';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { BreakEffect } from '../effects/BreakEffect';
import { InvalidEffect } from '../effects/InvalidEffect';
import { CollideHandler, ICollision } from './ICollision';

// fields
//-----------------------------------------------------------------------------
//const sounds = ['puzzle-lock-destroy', 'puzzle-lock-poke'];

/*
    chameleon block collision resolver
*/
export class ChameleonCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: ChameleonBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this._resolveTap,
    block: this._resolveBlock,
    attack: this._resolveAttack,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: ChameleonBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap(event: CollideEvent) {
    // invalid effect
    await new InvalidEffect(this._entity).execute();
    return false;
  }

  private async _resolveBlock(event: CollideEvent) {
    const collider = event.collider;

    // if block collider
    if (collider?.type === 'block') {
      const colliderProps = collider.entity.c.block.props;

      // if basic block collider and matches chameleon current color
      if (
        colliderProps.type === 'basic' &&
        this._entity.c.blockChameleon.activeColor === colliderProps.color
      ) {
        await this._attack();
        return true;
      }
    }
    return false;
  }

  private async _resolveAttack(event: CollideEvent) {
    // attack
    await this._attack();
    return true;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _attack() {
    const entity = this._entity;
    const position = entity.c.position.mapPosition;
    const component = entity.c.blockChameleon;

    // play sound based on component count
    //app.sound.play(sounds[component.count], { pan: mapGetPan(this._entity.c.position.mapPosition) });

    // despawn block
    despawnBlockEntity(this._scene, this._entity);

    // execute break effect
    await new BreakEffect(this._scene, {
      position,
      size: entity.c.block.props,
      color: blockColorMap[component.activeColor].base,
    }).execute();
  }
}
